import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ALL_PAYMENT_METHODS as PAYMENT_METHODS, TIERS } from "../lib/i18n";
import SyncedCasinoWheel from "../components/SyncedCasinoWheel";
import { toast } from "sonner";
import { Trophy, Users, HandHeart, Clock, LogOut, Sparkles } from "lucide-react";

const POLL_INTERVAL_MS = 1500;

export default function PoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { fmt } = useCurrency();
  const { user, refreshUser } = useAuth();
  const [pool, setPool] = useState(null);
  const [drawState, setDrawState] = useState(null);
  const [method, setMethod] = useState("zaincash");
  const [countdown, setCountdown] = useState(null);
  const [lastDrawSeen, setLastDrawSeen] = useState(null);
  const [exited, setExited] = useState(false);
  const lastDrawIdRef = useRef(null);

  // Initial load of full pool
  const loadFull = async () => {
    try {
      const { data } = await api.get(`/pools/${id}`);
      setPool(data);
      if (data.last_draw) lastDrawIdRef.current = data.last_draw.draw_id;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load pool");
    }
  };

  // Light polling: every 1.5s pull draw-state (small payload)
  const pollDrawState = async () => {
    try {
      const { data } = await api.get(`/pools/${id}/draw-state`);
      setDrawState(data);
      // Detect new draw
      if (data.last_draw && data.last_draw.draw_id !== lastDrawIdRef.current) {
        lastDrawIdRef.current = data.last_draw.draw_id;
        setLastDrawSeen(data.last_draw.draw_id);
        // Refresh user balance after draw
        setTimeout(() => refreshUser(), 6000);
        // Toast for non-spectators (members)
        const isMember = data.members?.some((m) => m.user_id === user?.user_id);
        if (isMember) {
          if (data.last_draw.winner_id === user?.user_id) {
            toast.success(`🤝 جاء دورك! +$${data.last_draw.payout.toFixed(2)}`);
          } else {
            toast.info(`📢 ${lang === "ar" ? "الفائز" : "Winner"}: ${data.last_draw.winner_name}`);
          }
        }
        // Reload full pool to refresh members.won flags
        loadFull();
      }
    } catch {
      /* ignore transient errors during polling */
    }
  };

  useEffect(() => { loadFull(); /* eslint-disable-next-line */ }, [id]);

  // Real-time poll loop (1.5s) while pool is active OR awaiting first draw
  useEffect(() => {
    if (!pool) return;
    if (pool.status === "completed") {
      // One last poll then stop
      pollDrawState();
      return;
    }
    pollDrawState();
    const i = setInterval(pollDrawState, POLL_INTERVAL_MS);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool?.status, id]);

  // Countdown overlay 5-4-3-2-1
  useEffect(() => {
    const src = drawState || pool;
    if (!src?.next_round_at || src.status !== "active") { setCountdown(null); return; }
    const tick = () => {
      const diff = (new Date(src.next_round_at) - new Date()) / 1000;
      if (diff > 0 && diff <= 5) setCountdown(Math.ceil(diff));
      else setCountdown(null);
    };
    tick();
    const i = setInterval(tick, 200);
    return () => clearInterval(i);
  }, [drawState?.next_round_at, pool?.next_round_at, drawState?.status, pool?.status]);

  if (!pool) return <div className="p-10 text-[#8FA3BD]">...</div>;

  const tier = pool.tier || "bronze";
  const tierInfo = TIERS[tier] || TIERS.admin;
  const liveMembers = drawState?.members || pool.members;
  const isMember = liveMembers.some((m) => m.user_id === user?.user_id);
  const isFull = liveMembers.length >= pool.member_count;
  const currentRound = drawState?.current_round || pool.current_round || 0;
  const status = drawState?.status || pool.status;
  const lastDraw = drawState?.last_draw || pool.last_draw;
  const draws = drawState?.draws || pool.draws || [];

  // Current user's slot
  const myMember = liveMembers.find((m) => m.user_id === user?.user_id);
  const myWonDraw = draws.find((d) => d.winner_id === user?.user_id);

  const handleJoin = async () => {
    try {
      await api.post(`/pools/${id}/join`, { payment_method: method });
      toast.success(t("join") + " ✓");
      await loadFull(); await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleExit = async () => {
    // Pool completed → user has already been credited (auto-credit on draw).
    // The "exit" simply navigates them out and confirms payout receipt.
    setExited(true);
    await refreshUser();
    toast.success(`✓ ${lang === "ar" ? "تم استلام أرباحك في المحفظة" : "Winnings credited to your wallet"}`);
    setTimeout(() => navigate("/wallet"), 1200);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Countdown overlay */}
      {countdown !== null && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center backdrop-blur-sm" data-testid="countdown-overlay">
          <div className="text-center">
            <Sparkles className="w-12 h-12 text-[#D4AF37] mx-auto mb-3 animate-pulse" />
            <div className="text-9xl font-bold text-cyan-gradient animate-pulse" data-testid="countdown-number">{countdown}</div>
            <div className="text-xl text-[#00E5FF] mt-4">
              {lang === "ar" ? "القرعة على وشك البدء..." : "The draw is about to begin..."}
            </div>
          </div>
        </div>
      )}

      {/* Winner banner */}
      {lastDraw && (
        <div className="bento-card p-5 mb-6 border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/10 to-transparent animate-fade-up" data-testid="last-draw-banner">
          <div className="flex items-center gap-3">
            <Trophy className="w-10 h-10 text-[#D4AF37]" />
            <div className="flex-1">
              <div className="text-xs text-[#8FA3BD]">{lang === "ar" ? "الفائز بالجولة" : "Winner of round"} {lastDraw.round} {lastDraw.auto_credit ? "(تلقائي)" : ""}</div>
              <div className="text-xl font-bold text-[#D4AF37]" data-testid="last-winner">{lastDraw.winner_name}</div>
              <div className="text-sm text-[#00E676]">+${lastDraw.payout.toFixed(2)}</div>
            </div>
            {lastDraw.winner_id === user?.user_id && (
              <div className="text-[10px] px-2 py-1 rounded-full bg-[#00E676]/20 text-[#00E676] font-bold">
                🎉 {lang === "ar" ? "أنت!" : "YOU!"}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient">{pool.name}</h1>
            <span className={`text-xs px-3 py-1 rounded-full ${tierInfo.cls}`}>{lang === "ar" ? tierInfo.label_ar : tierInfo.label_en}</span>
          </div>
          <p className="text-sm text-[#8FA3BD]">{pool.description}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="px-2 py-1 rounded bg-[#1A2D4D] text-[#8FA3BD]">{t(status)}</span>
            <span className="px-2 py-1 rounded bg-[#00E5FF]/15 text-[#00E5FF]">{fmt(pool.pot_per_round)}/{t("round")}</span>
            <span className="px-2 py-1 rounded bg-[#1A2D4D] text-[#8FA3BD]">{t(pool.frequency || "daily")}</span>
            <span className="px-2 py-1 rounded bg-[#1A2D4D] text-[#8FA3BD]">{t("round")} {currentRound || "—"}/{pool.member_count}</span>
            <span className="px-2 py-1 rounded bg-[#00E676]/15 text-[#00E676] flex items-center gap-1" data-testid="live-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" /> Live
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isMember && !isFull && status === "open" && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="btn-cyan" data-testid="join-pool-btn">{t("join")}</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
                <DialogHeader><DialogTitle>{lang === "ar" ? "الانضمام للسلفة" : "Join Pool"}</DialogTitle></DialogHeader>
                <p className="text-sm text-[#8FA3BD]">{lang === "ar" ? "المبلغ اليومي" : "Daily amount"}: <b className="text-[#00E5FF]">${pool.amount_per_round}</b></p>
                <div className="p-3 rounded-lg bg-[#FF9500]/10 border border-[#FF9500]/30 text-sm">
                  ⚠ {lang === "ar" ? "سيُخصم" : "Will deduct"} <b className="text-[#FF9500]">${(pool.amount_per_round * pool.member_count).toFixed(2)}</b> {lang === "ar" ? "من رصيدك فوراً كأمانة." : "from your balance immediately as escrow."}
                </div>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="payment-method-select" className="bg-[#050E1A] border-[#1A2D4D]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
                    {PAYMENT_METHODS.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`pm-${p.id}`}>{p.icon} {lang === "ar" ? p.label_ar : p.label_en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button onClick={handleJoin} className="btn-cyan w-full" data-testid="confirm-join-btn">
                    {lang === "ar" ? "تثبيت ودفع الأمانة" : "Confirm & Pay Escrow"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* خروج / Exit button when pool is completed AND user is a member who has won */}
          {status === "completed" && isMember && myWonDraw && !exited && (
            <Button onClick={handleExit} className="btn-gold" data-testid="exit-pool-btn">
              <LogOut className="w-4 h-4 me-1.5" />
              {lang === "ar" ? "خروج واستلام الأرباح" : "Exit & Claim Winnings"}
            </Button>
          )}
        </div>
      </div>

      {status === "completed" && (
        <div className="bento-card p-4 mb-6 flex items-center gap-3 border-[#D4AF37]/30" data-testid="pool-ended-banner">
          <Trophy className="w-5 h-5 text-[#D4AF37]" />
          <div className="text-sm text-[#D4AF37]">
            {lang === "ar" ? "اكتملت السلفة. جميع الأعضاء استلموا أدوارهم." : "Pool completed. All members received their turn."}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8 mb-10 items-center">
        <div className="bento-card p-6 sm:p-8 flex flex-col items-center">
          <SyncedCasinoWheel
            members={liveMembers}
            lastDraw={lastDraw}
            currentUserId={user?.user_id}
            frequencyMs={5000}
          />
          {drawState?.next_round_at && status === "active" && countdown === null && (
            <div className="mt-6 text-center text-sm text-[#8FA3BD]">
              <Clock className="w-4 h-4 inline-block me-1" />
              {lang === "ar" ? "القرعة القادمة" : "Next draw"}: {new Date(drawState.next_round_at).toLocaleString()}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-[#00E5FF]" /> {t("members")} ({liveMembers.length}/{pool.member_count})</h3>
          <div className="space-y-2">
            {liveMembers.map((m, i) => {
              const isWinner = lastDraw && lastDraw.winner_id === m.user_id;
              return (
                <div key={i} className={`bento-card p-4 flex items-center gap-3 ${isWinner ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : ""}`} data-testid={`member-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-[#1A2D4D] flex items-center justify-center text-sm font-bold text-[#00E5FF]">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{m.name} {m.user_id === user?.user_id && <span className="text-[10px] text-[#00E5FF]">({lang === "ar" ? "أنت" : "you"})</span>}</div>
                    <div className="text-xs text-[#4A5970]">{m.payment_method || "—"}</div>
                  </div>
                  {m.won && <Trophy className="w-4 h-4 text-[#D4AF37]" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {draws.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">{t("draws")}</h3>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr>
                  <th className="p-3 text-start">{t("round")}</th>
                  <th className="p-3 text-start">{t("winner")}</th>
                  <th className="p-3 text-start">{t("payout")}</th>
                  <th className="p-3 text-start">{lang === "ar" ? "النوع" : "Type"}</th>
                </tr>
              </thead>
              <tbody>
                {draws.map((d) => (
                  <tr key={d.draw_id} className="border-t border-[#1A2D4D]" data-testid={`draw-row-${d.draw_id}`}>
                    <td className="p-3">{d.round}</td>
                    <td className="p-3">{d.winner_name}</td>
                    <td className="p-3 text-[#00E5FF]">{fmt(d.payout)}</td>
                    <td className="p-3 text-xs">
                      {d.auto_credit
                        ? <span className="px-2 py-0.5 rounded bg-[#00E676]/15 text-[#00E676]">{lang === "ar" ? "تلقائي" : "Auto"}</span>
                        : <span className="px-2 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">{lang === "ar" ? "قرعة" : "Spin"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
